import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Location } from '@angular/common';
import { catchError, combineLatest, EMPTY, map, Observable, take } from 'rxjs';
import { Router } from '@angular/router';

import {
  PortalFeatures,
  PortalLeadNode,
  PortalOrderNode,
  PortalPassengerNodeInput,
} from '@app/services/api/api.types';
import { Steps } from '@app/components/stepper/stepper.component';
import { PortalLeadService } from '@app/services/portal-lead/portal-lead.service';
import { PortalOrderService } from '@app/services/portal-order/portal-order.service';
import { State, StateService } from '@app/services/state/state.service';
import { mapPortalPassengerNode } from '@app/utils/utils';
import { ApiService } from '@app/services/api/api.service';
import { Option } from '@app/forms/formly/formly-utils';
import {
  PopupsService,
  PriceSummaryAction,
  dropdownSeparatorValue,
} from '@voyagu/portal-design-system';
import { newTravalerOption } from '@app/utils/constants';
import { PortalTrackingEvents } from '@app/utils/tracking-events';
import { Tracking } from '@app/services/tracking/tracking.service';
import { AuthorizedDataService } from '@app/services/authorized-data/authorized-data.service';
import { PassengersFormComponent } from '@app/forms/passengers-form/passengers-form.component';
import { PortalRoutes } from '@app/app-routing.module';
import { isTiersFeature } from '@app/pages/tiers-page/utils';
import { MemoizedValue } from '@app/utils/memoized-value.util';

@Component({
  selector: 'passengers-page',
  templateUrl: './passengers-page.component.html',
  styleUrls: ['./passengers-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PassengersPageComponent implements OnInit {
  Steps = Steps;

  portalLead$: Observable<PortalLeadNode | null>;

  portalOrder: PortalOrderNode;
  state: State;
  passengers: Option[];

  passengersLabels: string[] = [];

  model: { passengers: PortalPassengerNodeInput[] } = { passengers: [] };
  errors: PassengersErrors | null;

  formHasChanges: boolean;
  withPayAndConfirm: boolean;

  portalTiers: boolean;

  @ViewChild('confirmTemplate') confirmTemplate: TemplateRef<unknown>;
  @ViewChild(PassengersFormComponent) passengersForm: PassengersFormComponent;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private authorizedDataService: AuthorizedDataService,
    private portalLeadService: PortalLeadService,
    private portalOrderService: PortalOrderService,
    private stateService: StateService,
    private popupsService: PopupsService,
    private apiService: ApiService,
    private location: Location,
    private router: Router,
  ) {}

  ngOnInit() {
    this.getPortalLead();
  }

  getPortalLead() {
    this.portalLead$ = combineLatest([
      this.portalLeadService.portalLead$,
      this.portalOrderService.portalOrder$,
    ]).pipe(
      map(([portalLead, portalOrder]) => {
        if (portalLead) {
          this.withPayAndConfirm = !portalOrder?.isDemo;

          this.portalTiers =
            portalLead.salesAgent?.portalFeatures?.includes(PortalFeatures.portal_tiers) &&
            isTiersFeature(portalOrder);
        }

        if (portalOrder) {
          this.portalOrder = portalOrder;
          this.model.passengers.length = portalOrder.totalPassengers;

          this.passengersLabels = makePassengersLabels(portalOrder);
        }

        this.state = this.stateService.getState();

        const mapped =
          this.authorizedDataService
            .getAuthorizedDataValue()
            ?.passengers?.map(mapPortalPassengerNode) || [];

        this.passengers = mapped.length
          ? [{ ...newTravalerOption }, { value: dropdownSeparatorValue, label: '' }, ...mapped]
          : [];

        if (this.state.passengers.length) {
          this.model = {
            passengers: this.state.passengers.map((item) => ({
              ...item,
              id: item.id || newTravalerOption.value,
            })),
          };
        }

        Tracking.track(PortalTrackingEvents.portalCheckoutPassengersOpened);

        return portalLead;
      }),
    );
  }

  valueChange() {
    this.formHasChanges = true;
  }

  tryBack() {
    if (this.formHasChanges) {
      this.popupsService.showModal(this.confirmTemplate);
      return;
    }

    this.back();
  }

  back() {
    this.location.back();
  }

  summaryActionsCache = new MemoizedValue<PriceSummaryAction[]>();

  get summaryActions(): PriceSummaryAction[] {
    const isSubmitting = !!this.passengersForm?.form.disabled;

    return this.summaryActionsCache.get(String(isSubmitting), () => [
      {
        id: 'continue',
        label: 'Continue',
        variant: 'primary',
        disabled: isSubmitting,
      },
      { id: 'back', label: 'Back', variant: 'secondary' },
    ]);
  }

  onSummaryAction(action: PriceSummaryAction) {
    if (action.id === 'back') {
      this.tryBack();
      return;
    }

    this.passengersForm?.submit();
  }

  formSubmit(data: { passengers: PortalPassengerNodeInput[] }) {
    const input = {
      portalLink: this.portalOrder.portalLink,
      passengers: data.passengers.map((p) => ({ ...p })),
    };

    input.passengers.forEach((p) => {
      if (p.id === newTravalerOption.value) {
        delete (p as Partial<{ id: unknown }>)['id'];
      }
    });

    return this.withPayAndConfirm
      ? this.portalSubmitPassengers({ input })
      : this.portalSubmitOption(input);
  }

  portalSubmitOption(input: { passengers: PortalPassengerNodeInput[]; portalLink: string }) {
    return this.apiService
      .portalSubmitOption({ input })
      .pipe(
        take(1),
        catchError((error: Error) => {
          console.log(error.message || error);
          Tracking.track(PortalTrackingEvents.portalCheckoutSubmitOptionError);
          return EMPTY;
        }),
      )
      .subscribe((response) => {
        if (response?.data?.portalSubmitOption?.ok) {
          this.portalLeadService.getPortalLead({ portalLink: this.state.portalLink });

          this.continue();
        } else {
          this.errors = JSON.parse(response?.data?.portalSubmitOption?.errors) as PassengersErrors;
        }

        this.changeDetectorRef.detectChanges();
      });
  }

  continue() {
    const path = [
      `${this.state.portalLink}`,
      `${this.portalOrder.portalLink}`,
      this.withPayAndConfirm ? PortalRoutes.Gratuity : PortalRoutes.Complete,
    ];

    void this.router.navigate(path, { queryParamsHandling: 'merge' });
  }

  portalSubmitPassengers(variables: {
    input: {
      portalLink: string;
      passengers: PortalPassengerNodeInput[];
    };
  }) {
    return this.apiService
      .portalSubmitPassengers(variables)
      .pipe(
        take(1),
        catchError((error: Error) => {
          console.log(error.message || error);
          Tracking.track(PortalTrackingEvents.portalCheckoutSubmitPassengersError);
          return EMPTY;
        }),
      )
      .subscribe((response) => {
        if (response?.ok) {
          this.errors = null;

          this.stateService.patchState({ passengers: variables.input.passengers });

          if (response.passengers?.length) {
            this.authorizedDataService.patchAuthorizedData({ passengers: response.passengers });
          }

          this.continue();
        } else {
          this.errors = JSON.parse(response.errors) as PassengersErrors;
        }

        this.changeDetectorRef.detectChanges();
      });
  }
}

export enum PassengerTypeLabel {
  Adult = 'Adult',
  Child = 'Child, 2-11',
  Infant = 'Infant, under age 2',
}

export type PassengersErrors = {
  passengers?: {
    birthday?: string;
  }[];
};

const makePassengersLabels = (portalOrder: PortalOrderNode) => {
  const passengersLabels = [];

  if (portalOrder) {
    for (let i = 0; i < portalOrder.totalPassengers; i++) {
      const label =
        i < portalOrder.numberOfAdults
          ? PassengerTypeLabel.Adult
          : i < portalOrder.numberOfAdults + portalOrder.numberOfChildren
            ? PassengerTypeLabel.Child
            : PassengerTypeLabel.Infant;

      passengersLabels.push(`Traveler ${i + 1}: ${label}`);
    }
  }

  return passengersLabels;
};
